'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';

const CARD_WIDTH = 340;
const GAP = 14;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function resolveBubblePosition(targetRect, placement = 'bottom') {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const margin = 16;

  const centerX = targetRect.left + targetRect.width / 2;
  const centerY = targetRect.top + targetRect.height / 2;

  let top = centerY;
  let left = centerX;

  if (placement === 'top') {
    top = targetRect.top - GAP;
    left = centerX - CARD_WIDTH / 2;
  }

  if (placement === 'bottom') {
    top = targetRect.bottom + GAP;
    left = centerX - CARD_WIDTH / 2;
  }

  if (placement === 'left') {
    top = centerY - 120;
    left = targetRect.left - CARD_WIDTH - GAP;
  }

  if (placement === 'right') {
    top = centerY - 120;
    left = targetRect.right + GAP;
  }

  const maxLeft = viewportWidth - CARD_WIDTH - margin;
  const maxTop = viewportHeight - 220;

  return {
    top: clamp(top, margin, Math.max(margin, maxTop)),
    left: clamp(left, margin, Math.max(margin, maxLeft)),
  };
}

export default function OnboardingTour({
  steps = [],
  isOpen,
  onClose,
  labels,
  initialStep = 0,
}) {
  const t = useTranslations('OnboardingTour');
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStep);
  const [targetRect, setTargetRect] = useState(null);
  const [bubbleStyle, setBubbleStyle] = useState({ top: 24, left: 24 });

  const ui = {
    previous: labels?.previous || t('tour.back'),
    next: labels?.next || t('tour.next'),
    close: labels?.close || t('close'),
    finish: labels?.finish || t('tour.finish'),
    step: labels?.step || t('tour.step'),
  };

  const activeStep = useMemo(() => {
    if (!Array.isArray(steps) || steps.length === 0) return null;
    return steps[currentStepIndex] || null;
  }, [steps, currentStepIndex]);

  useEffect(() => {
    if (!isOpen) return;
    setCurrentStepIndex(clamp(initialStep, 0, Math.max(0, steps.length - 1)));
  }, [isOpen, initialStep, steps.length]);

  useEffect(() => {
    if (!isOpen || !activeStep?.target) return undefined;

    const previous = document.querySelectorAll('[data-tour-active="true"]');
    previous.forEach((node) => node.removeAttribute('data-tour-active'));

    const el = document.querySelector(activeStep.target);
    if (el) {
      el.setAttribute('data-tour-active', 'true');
    }

    return () => {
      if (el) el.removeAttribute('data-tour-active');
    };
  }, [isOpen, activeStep]);

  useEffect(() => {
    if (!isOpen || !activeStep) return;

    let retryCount = 0;
    let retryTimeoutId = null;
    let initialTimeoutId = null;

    function recalculate() {
      const el = document.querySelector(activeStep.target);

      if (!el) {
        if (retryCount < 4) {
          retryCount += 1;
          retryTimeoutId = window.setTimeout(recalculate, 300);
          return;
        }

        setTargetRect(null);
        setBubbleStyle({ top: 24, left: 24 });
        return;
      }

      el.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });

      const rect = el.getBoundingClientRect();
      setTargetRect(rect);
      setBubbleStyle(resolveBubblePosition(rect, activeStep.placement || 'bottom'));
    }

    initialTimeoutId = window.setTimeout(recalculate, 300);
    window.addEventListener('resize', recalculate);
    window.addEventListener('scroll', recalculate, true);

    return () => {
      if (initialTimeoutId) window.clearTimeout(initialTimeoutId);
      if (retryTimeoutId) window.clearTimeout(retryTimeoutId);
      window.removeEventListener('resize', recalculate);
      window.removeEventListener('scroll', recalculate, true);
    };
  }, [isOpen, activeStep]);

  useEffect(() => {
    if (isOpen) return undefined;
    document.querySelectorAll('[data-tour-active="true"]').forEach((node) => {
      node.removeAttribute('data-tour-active');
    });
    return undefined;
  }, [isOpen]);

  if (!isOpen || !activeStep) return null;

  const isFirst = currentStepIndex === 0;
  const isLast = currentStepIndex === steps.length - 1;

  const localizedStepTitle = (() => {
    const key = `steps.${currentStepIndex}.title`;
    try {
      const translated = t(key);
      if (!translated || translated === key || translated.includes('OnboardingTour.steps.')) {
        return activeStep.title;
      }
      return translated;
    } catch {
      return activeStep.title;
    }
  })();

  const localizedStepContent = (() => {
    const key = `steps.${currentStepIndex}.content`;
    try {
      const translated = t(key);
      if (!translated || translated === key || translated.includes('OnboardingTour.steps.')) {
        return activeStep.content;
      }
      return translated;
    } catch {
      return activeStep.content;
    }
  })();

  function goPrevious() {
    if (isFirst) return;
    setCurrentStepIndex((prev) => prev - 1);
  }

  function goNext() {
    if (isLast) {
      onClose?.();
      return;
    }
    setCurrentStepIndex((prev) => prev + 1);
  }

  return (
    <div className="fixed inset-0 z-[200]">
      {/* Dim via cutout only — no full overlay on top of the target (that made tools unreadable). */}
      {targetRect ? (
        <>
          <div
            aria-hidden
            className="pointer-events-none fixed rounded-2xl transition-all duration-200"
            style={{
              top: targetRect.top - 10,
              left: targetRect.left - 10,
              width: targetRect.width + 20,
              height: targetRect.height + 20,
              boxShadow: '0 0 0 9999px rgba(2, 6, 23, 0.78)',
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none fixed rounded-2xl border-2 border-cyan-300 bg-gradient-to-b from-cyan-300/25 via-white/10 to-cyan-400/15 shadow-[0_0_48px_rgba(34,211,238,0.75),inset_0_0_28px_rgba(165,243,252,0.35)] transition-all duration-200"
            style={{
              top: targetRect.top - 10,
              left: targetRect.left - 10,
              width: targetRect.width + 20,
              height: targetRect.height + 20,
            }}
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-slate-950/70" aria-hidden />
      )}

      <div
        className="fixed w-[min(340px,calc(100vw-2rem))] rounded-2xl border border-cyan-400/40 bg-slate-900/95 p-4 text-slate-100 shadow-2xl backdrop-blur-xl"
        style={{ top: bubbleStyle.top, left: bubbleStyle.left, zIndex: 220 }}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-cyan-300">
              {ui.step} {currentStepIndex + 1}/{steps.length}
            </p>
            <h3 className="mt-1 text-base font-extrabold text-white">{localizedStepTitle}</h3>
          </div>
          <button
            type="button"
            onClick={() => onClose?.()}
            className="rounded-lg border border-slate-700 bg-slate-800/80 px-2.5 py-1 text-xs font-semibold text-slate-300 transition hover:border-slate-500 hover:text-white"
          >
            {ui.close}
          </button>
        </div>

        <p className="mb-4 text-sm leading-relaxed text-slate-300">{localizedStepContent}</p>

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={goPrevious}
            disabled={isFirst}
            className="rounded-xl border border-slate-700 bg-slate-800/80 px-3 py-2 text-sm font-semibold text-slate-200 transition hover:border-slate-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {ui.previous}
          </button>

          <button
            type="button"
            onClick={goNext}
            className="rounded-xl border border-cyan-400/40 bg-cyan-500/15 px-3 py-2 text-sm font-semibold text-cyan-200 transition hover:bg-cyan-500/25"
          >
            {isLast ? ui.finish : ui.next}
          </button>
        </div>
      </div>
    </div>
  );
}
